/**
 * @copyright
 * Copyright (c) 2024 Accretive Technology Group, Inc. All rights
 * reserved. For use only by Accretive Technology, its employees
 * and contractors. DO NOT DISTRIBUTE.
 */
import Drawer from "@atg/icf-react-components/components/Drawer";
import IconButton from "@atg/icf-react-components/components/IconButton";
import { SideMenu } from "@atg/icf-react-components/components/SideMenu";
import ITheme from "@atg/icf-react-components/themes/Theme";
import { FC, memo, useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { createUseStyles } from "react-jss";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

import { DEVICE_BP_DESKTOP_MIN, JSS_MEDIA_DESKTOP_MIN } from "~/constants";
import { selectAuthAccount, selectAuthHasOneClick, selectAuthViewOnly } from "~/features/auth/selectors";
import { getGuestMobileMenuItems, useAuthenticatedMobileMenuItems } from "~/features/header/utils/getMenuItems";
import { selectUnsavedChangesData } from "~/features/unsavedChanges/selectors";
import { UnsavedChangesActions } from "~/features/unsavedChanges/slice";
import useOnPathChange from "~/hooks/useOnPathChange";

interface StyleProps {
  authenticated: boolean;
}

const useStyles = createUseStyles<string, StyleProps, ITheme>((theme) => ({
  mobileMenu: {
    [JSS_MEDIA_DESKTOP_MIN]: {
      display: "none",
    },
  },
  icon: {
    "& svg": {
      fill: theme.colors.textOnDark,
    },

    "&:hover svg": {
      fill: theme.colors.baseInvariant,
    },
  },
  top: {
    display: "flex",
    justifyContent: "flex-end",
    alignItems: "center",
    width: "100%",
    height: "40px",
    // eslint-disable-next-line @atg/css-rtl-support
    padding: `${theme.spacing.base}px ${theme.spacing.base}px 0 0`,
    marginBottom: -theme.spacing.base,
    boxSizing: "border-box",
  },
  content: {
    minWidth: "260px",

    // sign up button background override
    "& div:nth-child(2) > div > div:nth-child(6)": {
      backgroundColor: (props) => (!props.authenticated ? theme.colors.primary : "initial"),
    },
  },
}));

const MobileMenu: FC = () => {
  const { t } = useTranslation("header");
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const viewOnly = useSelector(selectAuthViewOnly);
  const hasOneClick = useSelector(selectAuthHasOneClick);
  const account = useSelector(selectAuthAccount);
  const unsavedChanges = useSelector(selectUnsavedChangesData);

  const classes = useStyles({ authenticated: !!account });

  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  });

  const handleResize = () => {
    const width = window.innerWidth;

    if (width >= DEVICE_BP_DESKTOP_MIN) {
      setIsOpen(false);
    }
  };

  const onPathChange = useCallback(() => setIsOpen(false), []);

  const handleNavigate = (link: string) => {
    if (unsavedChanges.isDirty) {
      dispatch(UnsavedChangesActions.setShowModal(link));
    } else {
      void navigate(link);
    }
    setIsOpen(false);
  };

  const guestMenuItems = getGuestMobileMenuItems({ t, handleNavigate });

  const authenticatedMenuItems = useAuthenticatedMobileMenuItems({
    viewOnly,
    hasOneClick,
    t,
    handleNavigate,
  });

  useOnPathChange(onPathChange);

  return (
    <div className={classes.mobileMenu}>
      <IconButton
        className={classes.icon}
        name="menu"
        size="large"
        onClick={() => setIsOpen((currentIsOpen) => !currentIsOpen)}
      />

      <Drawer
        className={classes.drawer}
        anchor="right"
        gentleClose={true}
        open={isOpen}
        onClose={() => setIsOpen(false)}
      >
        <div className={classes.content}>
          <div className={classes.top}>
            <IconButton name="close" type="highContrast" onClick={() => setIsOpen(false)} />
          </div>

          <SideMenu menus={account ? authenticatedMenuItems : guestMenuItems} />
        </div>
      </Drawer>
    </div>
  );
};

export default memo(MobileMenu);
